import React, { useEffect, useState } from "react";
import {
  Button,
  Modal,
  ModalBody,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Form,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
} from "reactstrap";
import classnames from "classnames";
import Select from "react-select";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { isNumberOnly } from "helpers/GeneralHelper";
import {
  getCommonJobCategoryOptions,
  mapCommonPlanApiToSelectOptions,
} from "model/ClientRegisterModel";
import { submitQuestionTypeFilterData } from "model/QuestionTypeModel";
import { submitJobRequiredFilterData } from "model/JobRequiredModel";
import {
  addJob,
  getAIJobTypeOptions,
  getFadvPackageMultiOptions,
  getFadvPackageOptions,
  getJobDataById,
  mapAIJobTypeApiToSelectOptions,
  mapFadvPackageApiToSelectOptions,
  updateJob,
} from "model/JobModel";

const initialFormData = {
  id: null,
  name: "",
  nameCandidate: "",
  jobCategoryId: "",
  questionTypeId: "",
  weeklyTarget: 0,
  hireStatus: false,
  forFunnl: false,
  aiFadvPackageId: null,
  aiJobTypeId: null,
  fadvApiIds: [],
  color: "#000000",
  status: true,
};

const jobFormSchema = Yup.object().shape({
  name: Yup.string().trim().required("Name is required"),
  nameCandidate: Yup.string().trim().required("Name For Candidate is required"),
  jobCategoryId: Yup.string().required("Category is required"),
  questionTypeId: Yup.string().required("Question Type is required"),
  aiJobTypeId: Yup.string().nullable().required("AI Job Type is required"),
  weeklyTarget: Yup.number()
    .typeError("Weekly Target must be a number")
    .integer("Weekly Target must be a whole number")
    .min(1, "Weekly Target must be at least 1")
    .required("Weekly Target is required"),
  color: Yup.string().trim().required("Color is required"),
  hireStatus: Yup.boolean().required("Track Hire is required"),
  forFunnl: Yup.boolean().required("Show in Panel is required"),
  status: Yup.boolean().required("Status is required"),
});

const JobAddEditModal = ({
  setJobAddEditModalOpen,
  jobAddEditModalOpen,
  getJobList,
  itemId,
  setItemId,
}) => {
  const [formKey, setFormKey] = useState(0);
  const [activeTab, setActiveTab] = useState("1");
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [jobCategoryOptions, setJobCategoryOptions] = useState([]);
  const [questionTypeOptions, setQuestionTypeOptions] = useState([]);
  const [aiFadvPackageOptions, setAiFadvPackageOptions] = useState([]);
  const [aiJobTypeOptions, setAiJobTypeOptions] = useState([]);
  const [packageOptions, setPackageOptions] = useState([]);
  const [baseJobRequireRows, setBaseJobRequireRows] = useState([]);
  const [jobRequireRows, setJobRequireRows] = useState([]);
  const [selectedDetails, setSelectedDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const selectedRequirementsCount = jobRequireRows.filter(
    (row) =>
      Boolean(row?.requireForTool) ||
      Boolean(row?.requireForClient) ||
      Boolean(row?.isOptional)
  ).length;
  const selectZIndexStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 10050 }),
    menu: (base) => ({ ...base, zIndex: 10050 }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: "#0d1b6e",
      color: "#fff",
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: "#fff",
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: "#fff",
      ":hover": {
        backgroundColor: "rgba(255, 255, 255, 0.2)",
        color: "#fff",
      },
    }),
  };

  const toggleTab = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  const updateFormField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "weeklyTarget") {
      if (value === "" || isNumberOnly(value)) {
        updateFormField(name, value);
      }
      return;
    }

    updateFormField(name, type === "checkbox" ? checked : value);
  };

  const handleRequirementChange = (rowId, field) => {
    setJobRequireRows((prev) =>
      prev.map((row) =>
        String(row.id) === String(rowId)
          ? {
            ...row,
            [field]: !row[field],
          }
          : row
      )
    );
  };

  const closeModal = () => {
    setJobAddEditModalOpen(false);
    resetModalFormState();
    setItemId?.(null);
  };

  const resetModalFormState = () => {
    setFormData(initialFormData);
    setErrors({});
    setSelectedDetails([]);
    setBaseJobRequireRows([]);
    setJobRequireRows([]);
    setActiveTab("1");
    setFormKey((prev) => prev + 1);
  };

  const validateJobForm = async () => {
    try {
      await jobFormSchema.validate(formData, { abortEarly: false });
      setErrors({});
      return true;
    } catch (validationError) {
      const validationErrors = {};
      validationError?.inner?.forEach((errorItem) => {
        if (errorItem?.path && !validationErrors[errorItem.path]) {
          validationErrors[errorItem.path] = errorItem.message;
        }
      });
      setErrors(validationErrors);
      setActiveTab("1");
      return false;
    }
  };

  const parseBooleanValue = (value, fallback = false) => {
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value === 1;
    if (typeof value === "string") {
      const normalizedValue = value.trim().toLowerCase();
      if (["true", "1", "yes", "y", "active"].includes(normalizedValue)) return true;
      if (["false", "0", "no", "n", "deactive", "inactive"].includes(normalizedValue)) {
        return false;
      }
    }
    return fallback;
  };

  const mergeJobRequireWithDetails = (allJobRequireRows = [], jobRequireDetails = []) => {
    const detailsByJobRequireId = new Map(
      (jobRequireDetails || []).map((item) => [String(item?.jobRequireId), item])
    );

    return (allJobRequireRows || []).map((row, index) => {
      const matchedDetail = detailsByJobRequireId.get(String(row.id));
      return {
        ...row,
        id: row?.id || "",
        sort: matchedDetail?.sort ?? index,
        detailId: matchedDetail?.id || null,
        sendMailDaily: matchedDetail?.sendMailDaily ?? "no",
        emailHeader: matchedDetail?.emailHeader ?? null,
        clientId: matchedDetail?.clientId ?? null,
        requireForTool: Boolean(
          matchedDetail?.requireForTool ??
          matchedDetail?.tool ??
          matchedDetail?.forTool ??
          matchedDetail?.isForTool ??
          row?.requireForTool
        ),
        requireForClient: Boolean(
          matchedDetail?.requireForClient ??
          matchedDetail?.client ??
          matchedDetail?.forClient ??
          matchedDetail?.isForClient ??
          row?.requireForClient
        ),
        isOptional: Boolean(
          matchedDetail?.isOptional ??
          matchedDetail?.optional ??
          row?.isOptional
        ),
      };
    });
  };

  const fetchDropdownData = async () => {
    const [
      categoryResponse,
      questionTypeResponse,
      jobRequireResponse,
      aiFadvPackageResponse,
      aiJobTypeResponse,
    ] = await Promise.all([
      getCommonJobCategoryOptions(),
      submitQuestionTypeFilterData({}),
      submitJobRequiredFilterData({}),
      getFadvPackageOptions({ primaryIds: null }),
      getAIJobTypeOptions({}),
    ]);

    const categories = mapCommonPlanApiToSelectOptions(categoryResponse);

    const questionTypes = (questionTypeResponse?.data || []).map((item) => ({
      value: item?.id || item?.questionTypeId,
      label: item?.name || item?.questionTypeName,
    }));

    const jobRequireItemsForJob = (jobRequireResponse?.data || []).filter(
      (item) => Number(item?.forJob) === 1
    );

    const allJobRequireRows = jobRequireItemsForJob.map((item) => ({
      id: item?.id || item?.jobRequireId || item?.jobCategoryId || item?._id || "",
      type: item?.name || item?.jobCategoryName || item?.title || "",
      requireForTool: false,
      requireForClient: false,
      isOptional: false,
    }));

    const aiPackageOptions = mapFadvPackageApiToSelectOptions(aiFadvPackageResponse);
    const aiJobTypeSelectOptions = mapAIJobTypeApiToSelectOptions(aiJobTypeResponse);

    return {
      categories,
      questionTypes,
      allJobRequireRows,
      aiPackageOptions,
      aiJobTypeSelectOptions,
    };
  };

  useEffect(() => {
    if (!jobAddEditModalOpen) return;

    let isCancelled = false;

    const loadModalData = async () => {
      setLoading(true);
      try {
        const [dropdownData, jobResponse] = await Promise.all([
          fetchDropdownData(),
          itemId ? getJobDataById({ Id: itemId }) : Promise.resolve(null),
        ]);
        if (isCancelled) return;

        setJobCategoryOptions(dropdownData.categories);
        setQuestionTypeOptions(dropdownData.questionTypes);
        setAiFadvPackageOptions(dropdownData.aiPackageOptions);
        setAiJobTypeOptions(dropdownData.aiJobTypeSelectOptions);
        setBaseJobRequireRows(dropdownData.allJobRequireRows);

        if (!itemId) {
          resetModalFormState();
          const packageMultiResponse = await getFadvPackageMultiOptions({
            primaryIds: null,
            selected: [],
          });
          if (isCancelled) return;
          setPackageOptions(mapFadvPackageApiToSelectOptions(packageMultiResponse));
          setJobRequireRows(
            mergeJobRequireWithDetails(dropdownData.allJobRequireRows, [])
          );
          return;
        }

        const row = jobResponse?.data;
        if (!row) return;

        const jobRequireDetails = Array.isArray(row?.details)
          ? row.details
          : Array.isArray(row?.jobRequireDetails)
            ? row.jobRequireDetails
            : [];

        const selectedFadvApiIds = Array.isArray(row?.fadvApiIds) ? row.fadvApiIds : [];

        const packageMultiResponse = await getFadvPackageMultiOptions({
          primaryIds: null,
          selected: selectedFadvApiIds,
        });
        if (isCancelled) return;
        setPackageOptions(mapFadvPackageApiToSelectOptions(packageMultiResponse));

        setFormData({
          id: row?.id || itemId,
          name: row?.name || "",
          nameCandidate: row?.nameCandidate || "",
          jobCategoryId: row?.jobCategoryId || "",
          questionTypeId: row?.questionTypeId || "",
          weeklyTarget: Number(row?.weeklyTarget || 0),
          hireStatus: parseBooleanValue(row?.hireStatus, false),
          forFunnl: parseBooleanValue(row?.forFunnl, false),
          aiFadvPackageId: row?.aiFadvPackageId || null,
          aiJobTypeId: row?.aiJobTypeId || null,
          fadvApiIds: selectedFadvApiIds,
          color: row?.color || "#000000",
          status: row?.status === "active" ? true : false,
        });
        setSelectedDetails(jobRequireDetails);
        setJobRequireRows(
          mergeJobRequireWithDetails(dropdownData.allJobRequireRows, jobRequireDetails)
        );
        setErrors({});
        setActiveTab("1");
        setFormKey((prev) => prev + 1);
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    loadModalData();

    return () => {
      isCancelled = true;
    };
  }, [jobAddEditModalOpen, itemId]);

  useEffect(() => {
    if (!jobAddEditModalOpen) {
      resetModalFormState();
      setItemId?.(null);
    }
  }, [jobAddEditModalOpen]);

  const isJobRequireRowSelected = (row) =>
    Boolean(row?.requireForTool) ||
    Boolean(row?.requireForClient) ||
    Boolean(row?.isOptional);

  const buildJobRequirePayload = () => {
    const selectedRowsPayload = jobRequireRows
      .filter(
        (row) => isJobRequireRowSelected(row) || Boolean(row?.detailId)
      )
      .map((row, index) => ({
        id: row?.detailId || null,
        sort: Number.isFinite(row?.sort) ? row.sort : index,
        jobRequireId: row?.id,
        sendMailDaily: row?.sendMailDaily ?? "no",
        emailHeader: row?.emailHeader ?? null,
        clientId: row?.clientId ?? null,
        requireForTool: Boolean(row?.requireForTool),
        requireForClient: Boolean(row?.requireForClient),
        isOptional: Boolean(row?.isOptional),
      }));

    const selectedJobRequireIds = new Set(
      selectedRowsPayload.map((row) => String(row?.jobRequireId))
    );

    const preservedSelectedRows = (selectedDetails || [])
      .filter(
        (detail) =>
          (Boolean(detail?.requireForTool) ||
            Boolean(detail?.requireForClient) ||
            Boolean(detail?.isOptional)) &&
          !selectedJobRequireIds.has(String(detail?.jobRequireId))
      )
      .map((detail, index) => ({
        id: detail?.id || null,
        sort: Number.isFinite(detail?.sort)
          ? detail.sort
          : selectedRowsPayload.length + index,
        jobRequireId: detail?.jobRequireId || null,
        sendMailDaily: detail?.sendMailDaily ?? "no",
        emailHeader: detail?.emailHeader ?? null,
        clientId: detail?.clientId ?? null,
        requireForTool: Boolean(detail?.requireForTool),
        requireForClient: Boolean(detail?.requireForClient),
        isOptional: Boolean(detail?.isOptional),
      }));

    return [...selectedRowsPayload, ...preservedSelectedRows];
  };

  const handleSaveJob = async () => {
    const isFormValid = await validateJobForm();
    if (!isFormValid) return;

    const payload = {
      id: formData?.id || itemId || null,
      name: formData?.name,
      nameCandidate: formData?.nameCandidate,
      jobCategoryId: formData?.jobCategoryId,
      questionTypeId: formData?.questionTypeId,
      weeklyTarget: Number(formData?.weeklyTarget || 0),
      hireStatus: formData?.hireStatus,
      forFunnl: formData?.forFunnl,
      aiFadvPackageId: formData?.aiFadvPackageId || null,
      aiJobTypeId: formData?.aiJobTypeId || null,
      fadvApiIds: formData?.fadvApiIds || [],
      color: formData?.color || "#000000",
      status: formData?.status ? "active" : "deactive",
      jobRequireDetails: buildJobRequirePayload(),
    };

    const response = itemId
      ? await updateJob(itemId, payload)
      : await addJob(payload);

    if (response?.success) {
      toast.success(response?.message || "Job saved successfully");
      getJobList?.();
      closeModal();
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    await handleSaveJob();
  };

  return (
    <Modal
      isOpen={jobAddEditModalOpen}
      toggle={closeModal}
      backdrop="static"
      size="xl"
      centered
      zIndex={5000}
      className="modal-full-height"
    >
      <ModalBody
        key={formKey}
        className="p-4"
        style={{ maxHeight: "92vh", overflowY: "auto", backgroundColor: "#f8fafc" }}
      >
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div>
            <h5 className="fw-bold mb-1">{itemId ? "Edit" : "Create"} Job</h5>
            {/* <hr/> */}
            {/* <p className="text-muted mb-0">
              Configure job details, package mapping, and required checks.
            </p> */}
          </div>
          <Button close onClick={closeModal} />
        </div>

        <Nav tabs className="mb-4 border-0 custom-tabs">
          <NavItem>
            <NavLink
              className={classnames("tab-pill-job", {
                "tab-active-job": activeTab === "1",
                "tab-inactive-job": activeTab !== "1",
              })}
              onClick={() => toggleTab("1")}
            >
              Details
            </NavLink>
          </NavItem>

          <NavItem>
            <NavLink
              className={classnames("tab-pill-job", {
                "tab-active-job": activeTab === "2",
                "tab-inactive-job": activeTab !== "2",
              })}
              onClick={() => toggleTab("2")}
            >
              Job Require
            </NavLink>
          </NavItem>
        </Nav>

        <Form onSubmit={handleFormSubmit}>
          <TabContent activeTab={activeTab}>
            <TabPane tabId="1">
              <div className="card border-0 shadow-sm mb-3">
                <div className="card-body">
                  <h6 className="fw-semibold mb-3">Basic Information</h6>
                  <Row>
                    <Col md={8}>
                      <FormGroup>
                        <Label>Name <span className="text-danger">*</span></Label>
                        <Input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                        />
                        {errors?.name && <small className="text-danger">{errors.name}</small>}
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup>
                        <Label>Weekly Target <span className="text-danger">*</span></Label>
                        <Input
                          // type="number"
                          name="weeklyTarget"
                          value={formData.weeklyTarget}
                          onChange={handleChange}
                          min="1"
                          step="1"
                          onKeyDown={(e) => {
                            if (["e", "E", "+", "-", "."].includes(e.key)) {
                              e.preventDefault();
                            }
                          }}
                        />
                        {errors?.weeklyTarget && (
                          <small className="text-danger">{errors.weeklyTarget}</small>
                        )}
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={8}>
                      <FormGroup>
                        <Label>Name For Candidate <span className="text-danger">*</span></Label>
                        <Input
                          type="text"
                          name="nameCandidate"
                          value={formData.nameCandidate}
                          onChange={handleChange}
                        />
                        {errors?.nameCandidate && (
                          <small className="text-danger">{errors.nameCandidate}</small>
                        )}
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup>
                        <Label>Color</Label>
                        <div className="d-flex align-items-center gap-2">
                          <Input
                            type="color"
                            name="color"
                            value={formData.color}
                            onChange={handleChange}
                            className="colour-picker-stl"
                          />
                          <Input
                            type="text"
                            value={formData.color}
                            onChange={(e) => updateFormField("color", e.target.value)}
                          />
                        </div>
                        {errors?.color && <small className="text-danger">{errors.color}</small>}
                      </FormGroup>
                    </Col>
                  </Row>
                </div>
              </div>

              <div className="card border-0 shadow-sm mb-3">
                <div className="card-body">
                  <h6 className="fw-semibold mb-3">Configuration</h6>
                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label>Category <span className="text-danger">*</span></Label>
                        <Select
                          key={`job-category-${formKey}-${formData.jobCategoryId}`}
                          options={jobCategoryOptions}
                          className="select2-selection"
                          placeholder="Select Job Category"
                          value={
                            jobCategoryOptions.find(
                              (option) => String(option.value) === String(formData.jobCategoryId)
                            ) || null
                          }
                          onChange={(selectedOption) =>
                            updateFormField("jobCategoryId", selectedOption?.value || "")
                          }
                          menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                          menuPosition="fixed"
                          styles={selectZIndexStyles}
                        />
                        {errors?.jobCategoryId && (
                          <small className="text-danger">{errors.jobCategoryId}</small>
                        )}
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label>Question Type <span className="text-danger">*</span></Label>
                        <Select
                          key={`question-type-${formKey}-${formData.questionTypeId}`}
                          options={questionTypeOptions}
                          className="select2-selection"
                          placeholder="Select Question Type"
                          value={
                            questionTypeOptions.find(
                              (option) => String(option.value) === String(formData.questionTypeId)
                            ) || null
                          }
                          onChange={(selectedOption) =>
                            updateFormField("questionTypeId", selectedOption?.value || "")
                          }
                          menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                          menuPosition="fixed"
                          styles={selectZIndexStyles}
                        />
                        {errors?.questionTypeId && (
                          <small className="text-danger">{errors.questionTypeId}</small>
                        )}
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label>AI Fadv Package</Label>
                        <Select
                          isClearable
                          options={aiFadvPackageOptions}
                          className="select2-selection"
                          placeholder="Select FadvPackage For AI"
                          value={
                            aiFadvPackageOptions.find(
                              (option) => String(option.value) === String(formData.aiFadvPackageId)
                            ) || null
                          }
                          onChange={(selectedOption) =>
                            setFormData((prev) => ({
                              ...prev,
                              aiFadvPackageId: selectedOption?.value || null,
                            }))
                          }
                          menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                          menuPosition="fixed"
                          menuPlacement="auto"
                          styles={selectZIndexStyles}
                        />
                      </FormGroup>
                    </Col>

                    <Col md={6}>
                      <FormGroup>
                        <Label>Package</Label>
                        <Select
                          isMulti
                          isClearable
                          options={packageOptions}
                          className="select2-selection"
                          placeholder="Select Package"
                          value={packageOptions.filter((option) =>
                            (formData.fadvApiIds || [])
                              .map(String)
                              .includes(String(option.value))
                          )}
                          onChange={(selectedOptions) =>
                            setFormData((prev) => ({
                              ...prev,
                              fadvApiIds: (selectedOptions || []).map((option) => option.value),
                            }))
                          }
                          menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                          menuPosition="fixed"
                          menuPlacement="auto"
                          styles={selectZIndexStyles}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                  <Col md={6}>
                      <FormGroup>
                        <Label>AI Job Type <span className="text-danger">*</span></Label>
                        <Select
                          isClearable
                          options={aiJobTypeOptions}
                          className="select2-selection"
                          placeholder="Select AI Job Type"
                          value={
                            aiJobTypeOptions.find(
                              (option) => String(option.value) === String(formData.aiJobTypeId)
                            ) || null
                          }
                          onChange={(selectedOption) => {
                            const nextValue = selectedOption?.value || null;
                            setFormData((prev) => ({
                              ...prev,
                              aiJobTypeId: nextValue,
                            }));
                            if (errors?.aiJobTypeId) {
                              setErrors((prev) => ({ ...prev, aiJobTypeId: "" }));
                            }
                          }}
                          menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                          menuPosition="fixed"
                          menuPlacement="auto"
                          styles={selectZIndexStyles}
                        />
                        {errors?.aiJobTypeId && (
                          <small className="text-danger">{errors.aiJobTypeId}</small>
                        )}
                      </FormGroup>
                    </Col>
                   
                  </Row>
                </div>
              </div>

              <div className="card border-0 shadow-sm">
                <div className="card-body">
                  <h6 className="fw-semibold mb-3">Behavior & Status</h6>
                  <Row>
                    <Col md={4}>
                      <FormGroup>
                        <Label>Track Hire <span className="text-danger">*</span></Label>
                        <div className="border rounded-3 p-2 bg-white d-flex align-items-center justify-content-between">
                          <span className="text-muted small">Choose Track Hire</span>
                          <div>
                            <Input
                              key={`hireStatus-yes-${formKey}`}
                              type="radio"
                              name="hireStatus"
                              defaultChecked={formData?.hireStatus === true}
                              onChange={() => updateFormField("hireStatus", true)}
                            />{" "}
                            Yes
                            <Input
                              key={`hireStatus-no-${formKey}`}
                              type="radio"
                              name="hireStatus"
                              defaultChecked={formData?.hireStatus === false}
                              onChange={() => updateFormField("hireStatus", false)}
                              className="ms-3"
                            />{" "}
                            No
                          </div>
                        </div>
                        {errors?.hireStatus && (
                          <small className="text-danger">{errors.hireStatus}</small>
                        )}
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup>
                        <Label>Show in Panel <span className="text-danger">*</span></Label>
                        <div className="border rounded-3 p-2 bg-white d-flex align-items-center justify-content-between">
                          <span className="text-muted small">Choose Show in Panel</span>
                          <div>
                            <Input
                              key={`forFunnl-yes-${formKey}`}
                              type="radio"
                              name="forFunnl"
                              defaultChecked={formData?.forFunnl === true}
                              onChange={() => updateFormField("forFunnl", true)}
                            />{" "}
                            Yes
                            <Input
                              key={`forFunnl-no-${formKey}`}
                              type="radio"
                              name="forFunnl"
                              defaultChecked={formData?.forFunnl === false}
                              onChange={() => updateFormField("forFunnl", false)}
                              className="ms-3"
                            />{" "}
                            No
                          </div>
                        </div>
                        {errors?.forFunnl && (
                          <small className="text-danger">{errors.forFunnl}</small>
                        )}
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup>
                        <Label>Status <span className="text-danger">*</span></Label>
                        <div className="border rounded-3 p-2 bg-white d-flex align-items-center justify-content-between">
                          <span className="text-muted small">Select status</span>
                          <div>
                            <Input
                              key={`status-active-${formKey}`}
                              type="radio"
                              name="status"
                              defaultChecked={formData?.status === true}
                              onChange={() => updateFormField("status", true)}
                            />{" "}
                            Active
                            <Input
                              key={`status-deactive-${formKey}`}
                              type="radio"
                              name="status"
                              defaultChecked={formData?.status === false}
                              onChange={() => updateFormField("status", false)}
                              className="ms-3"
                            />{" "}
                            Deactive
                          </div>
                        </div>
                        {errors?.status && (
                          <small className="text-danger">{errors.status}</small>
                        )}
                      </FormGroup>
                    </Col>
                  </Row>
                </div>
              </div>

              <div className="text-end mt-4 text-end">
                <Button color="primary" type="submit" disabled={loading}>
                  Save
                </Button>
              </div>
            </TabPane>

            <TabPane tabId="2">
              <div className="card border-0 shadow-sm">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="fw-semibold mb-0">Job Requirements </h6>
                    {/* <span className="badge bg-primary-subtle text-primary px-3 py-2">
                      Selected: {selectedRequirementsCount}
                    </span> */}
                  </div>
                  <div className="table-responsive">
                    <table className="table table-bordered table-hover align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Type</th>
                          <th>Tool</th>
                          <th>Client</th>
                          <th>Optional</th>
                        </tr>
                      </thead>
                      <tbody>
                        {jobRequireRows.map((row, index) => (
                          <tr key={`${formKey}-${String(row.id || index)}`}>
                            <td>{row.type}</td>
                            <td>
                              <Input
                                key={`${row.id}-requireForTool-${formKey}`}
                                type="checkbox"
                                defaultChecked={Boolean(row.requireForTool)}
                                onChange={() =>
                                  handleRequirementChange(row.id, "requireForTool")
                                }
                              />
                            </td>
                            <td>
                              <Input
                                key={`${row.id}-requireForClient-${formKey}`}
                                type="checkbox"
                                defaultChecked={Boolean(row.requireForClient)}
                                onChange={() =>
                                  handleRequirementChange(row.id, "requireForClient")
                                }
                              />
                            </td>
                            <td>
                              <Input
                                key={`${row.id}-isOptional-${formKey}`}
                                type="checkbox"
                                defaultChecked={Boolean(row.isOptional)}
                                onChange={() =>
                                  handleRequirementChange(row.id, "isOptional")
                                }
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="mt-4 text-end">
                {/* <Button color="light" type="button" onClick={() => setActiveTab("1")}>
                  Back
                </Button> */}
                <Button color="primary" type="submit" disabled={loading}>
                  Save
                </Button>
              </div>
            </TabPane>
          </TabContent>
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default JobAddEditModal;
